import { useContext, useRef } from 'react';
import useRememberPosition, { type ScrollBarRef } from 'components/Dashboard/hooks/useRememberPosition';
import TagsLists from 'components/Dashboard/TagsList';
import { store } from 'components/Store';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemContent,
  ListItemHeader,
  ListItemLink,
} from 'components/Dashboard/ItemController/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import time from 'utils/time';
import { flattenListRowProps } from 'components/Dashboard/ListControl/listRowProps';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

interface GovernmentPublicationListItem {
  type: string;
  contentUrl: string;
  title: string;
  dateTime: string;
  dateTimeUpdated?: string | null;
  from?: string[];
  summary?: string;
  body?: string;
}

interface GovernmentPublicationItemProps {
  listRow: {
    listContext: { item: GovernmentPublicationListItem; scrollBarRef?: ScrollBarRef; index: number };
    feedOverrides?: { feedItemFlags?: unknown[] };
    display?: {
      hideYellowDot?: boolean;
      showContentTypeBadge?: boolean;
      notShowShare?: boolean;
      tagsFixedPosition?: boolean;
      tooltipText?: string;
    };
    interactions?: {
      itemOnclick?: (e: unknown) => void;
      thumbDownProps?: unknown;
      itemActionsProps?: unknown;
    };
  };
}

const GovernmentPublicationItem = ({ listRow }: GovernmentPublicationItemProps) => {
  const { state } = useContext(store) as unknown as { state: { search: { list: unknown; activeSearch: boolean } } };
  const { search } = state;
  const { list, activeSearch } = search;

  const { contentResults } = useContentResults() as unknown as {
    contentResults: { GovernmentPublications?: { feedItemFlags?: unknown[] } };
  };

  const { listContext, feedOverrides = {}, display = {}, interactions = {} } = listRow;
  const { item, scrollBarRef, index } = listContext;
  const feedItemFlags = feedOverrides.feedItemFlags ?? contentResults.GovernmentPublications?.feedItemFlags;
  const { hideYellowDot, showContentTypeBadge, notShowShare, tagsFixedPosition, tooltipText } = display;
  const { itemOnclick, thumbDownProps, itemActionsProps } = interactions;
  const tagsListsProps = flattenListRowProps(listRow);
  const { contentUrl, title, dateTime, dateTimeUpdated, from, summary } = item;

  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item: { ...item, body: summary ?? item.body } });
  const { rememberPosition } = useRememberPosition();

  const { unseenConditional, locationDescriptor } = useListItemController({
    item,
    index,
    feedItemFlags,
    itemFromWidget: undefined,
    listView: undefined,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge: undefined,
    itemName: undefined,
  });

  return (
    <ListItemLink
      to={locationDescriptor(contentUrl)}
      style={undefined}
      onClick={(e: unknown) => {
        if (scrollBarRef) {
          rememberPosition('GovernmentPublications', scrollBarRef, index);
        }
        if (itemOnclick) {
          itemOnclick(e);
        }
      }}
      tooltipText={tooltipText}
    >
      <ListItemContainer className='my-2 content-item' style={undefined} unseen={unseenConditional}>
        <ListItemContent>
          <ListItemHeader
            className='justify-content-between mb-2'
            mainContent={
              showContentTypeBadge ? (
                <div className='mt-1 specific-bottom-margin-4 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                  <ContentTypeBadge objectType={item.type} name={'Government publication'} />
                </div>
              ) : (
                <h4 className={`mb-0 flex-grow-1 content-item-title`}>{title}</h4>
              )
            }
            actions={
              !notShowShare ? (
                <ItemControllerActions
                  priorityBadge={null}
                  rateUpdatesMode={undefined}
                  ratingFeedback={undefined}
                  actions={
                    <GeneralItemActions
                      item={item}
                      index={index}
                      thumbDownProps={thumbDownProps}
                      itemActionsProps={itemActionsProps}
                    />
                  }
                />
              ) : null
            }
          />
          {showContentTypeBadge && <h4 className={`mb-2 flex-grow-1 content-item-title`}>{title}</h4>}
          <div className={`content-title w-100`}>
            <p className='heading-date mb-0'>
              {!!from?.length && (
                <>
                  {from.join(', ')}
                  <span className='dot' />
                </>
              )}
              <span className='date'>{time(dateTime).formatLongDate()}</span>
              {!!dateTimeUpdated && (
                <>
                  <span className='dot' />
                  <span className='date'>Last updated: {time(dateTimeUpdated).formatLongDate()}</span>
                </>
              )}
            </p>
            <p
              className='title-body my-2 position-relative'
              ref={titleBodyComponent}
              dangerouslySetInnerHTML={createMarkup(bodySnippet)}
            />
          </div>

          {list !== null && activeSearch && (
            <TagsLists
              {...tagsListsProps}
              feedItemFlags={feedItemFlags}
              index={index}
              section={'GovernmentPublications'}
              tagsFixedPosition={tagsFixedPosition}
              scrollBarRef={scrollBarRef}
            />
          )}
        </ListItemContent>
      </ListItemContainer>
    </ListItemLink>
  );
};

export default GovernmentPublicationItem;
